import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { HealthScoreGauge } from './HealthScoreGauge';
import { ActivityChart } from './ActivityChart';
import { ScoreDetailsDialog } from './ScoreDetailsDialog';
import { AISummary } from './AISummary';
import { InsightsSection } from './InsightsSection';
import { Brain } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { PillarsScore } from './PillarsScore';
import { HealthScore } from '@/types/healthScore';

interface AISummaryData {
  overall: string;
  insights: string[];
  recommendations: string[];
}

interface FamilyMember {
  id: string;
  name: string;
  relationship: string;
  score: number;
  avatar: string;
}

interface DashboardContentProps {
  selectedMember: string;
  familyMembers: FamilyMember[];
  currentUser: {
    name: string;
    lastUpdated: string;
  };
  displayName: string;
  aiSummary: AISummaryData | null;
  healthScore: HealthScore | null;
}

export const DashboardContent = ({ 
  selectedMember, 
  familyMembers, 
  currentUser, 
  displayName,
  aiSummary,
  healthScore
}: DashboardContentProps) => {
  const getCurrentMember = () => {
    return familyMembers.find(m => m.id === selectedMember) || familyMembers[0];
  };

  const insights = [
    {
      type: 'celebrate',
      title: 'Great progress on your fitness goals!',
      description: 'Your average daily steps increased by 15% this month.',
      action: 'Share achievement'
    },
    {
      type: 'action',
      title: 'Lab results available',
      description: 'Your recent cholesterol panel shows improvement.',
      action: 'View results'
    },
    {
      type: 'reminder',
      title: 'Annual checkup due',
      description: 'Schedule your appointment with Dr. Smith.',
      action: 'Schedule now'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Main Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Health Score Card */}
        <Card className="lg:col-span-1 p-6">
          <div className="text-center space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Health Score</h2>
              <p className="text-sm text-muted-foreground">
                Last updated {currentUser.lastUpdated}
              </p>
            </div>
            <HealthScoreGauge 
              score={healthScore?.total ?? 0}
              baselineScore={healthScore?.baseline ?? 0}
              actionableScore={(healthScore?.total ?? 0) - (healthScore?.baseline ?? 0)}
            />
            <ScoreDetailsDialog 
              memberName={getCurrentMember()?.name || displayName} 
              totalScore={healthScore?.total ?? 0}
              baselineScore={healthScore?.baseline ?? 0}
              actionableScore={(healthScore?.total ?? 0) - (healthScore?.baseline ?? 0)}
              healthScore={healthScore}
            >
              <Button className="w-full">
                View Score Details
              </Button>
            </ScoreDetailsDialog>
          </div>
        </Card>

        {/* AI Summary - Replacing Quick Metrics */}
        <div className="lg:col-span-2">
          {aiSummary ? (
            <Card className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Brain className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">AI Health Summary</h3>
              </div>
              <div className="space-y-4">
                <div className="prose prose-sm max-w-none">
                  <div className="text-sm text-muted-foreground">
                    <ReactMarkdown>{aiSummary.overall}</ReactMarkdown>
                  </div>
                </div>
                {aiSummary.insights && aiSummary.insights.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-foreground mb-2">Key Insights</p>
                    <ul className="space-y-1">
                      {aiSummary.insights.map((insight: string, index: number) => (
                        <li key={index} className="text-sm text-muted-foreground flex items-start">
                          <span className="text-primary mr-2">•</span>
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {aiSummary.recommendations && aiSummary.recommendations.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-foreground mb-2">Recommendations</p>
                    <ul className="space-y-1">
                      {aiSummary.recommendations.map((rec: string, index: number) => (
                        <li key={index} className="text-sm text-muted-foreground flex items-start">
                          <span className="text-primary mr-2">•</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Card>
          ) : (
            <Card className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Brain className="h-5 w-5 text-primary animate-pulse" />
                <h3 className="text-lg font-semibold">AI Health Summary</h3>
              </div>
              <div className="text-sm text-muted-foreground mb-3">
                AI is analyzing your health data...
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Activity Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">7-Day Health Trend</h3>
        <ActivityChart />
      </Card>

      {/* Actionable Health Pillars */}
      {healthScore && <PillarsScore scores={healthScore} />}

      {/* Insights & Actions */}
      <InsightsSection insights={insights} />
    </div>
  );
};
